import { useMutation, useQuery, useTransport, createConnectQueryKey } from '@connectrpc/connect-query'
import { useQueryClient } from '@tanstack/react-query'
import { create } from '@bufbuild/protobuf'
import { useStore } from '@tanstack/react-store'

import {
  getEventTimeline,
  listEventStaffAvailability,
  listJobEvents,
  regenerateEventTimeline,
  updateEventTimelineBlocks,
} from '@/gen/cortado/v1/cortado-CortadoService_connectquery'
import {
  GetEventTimelineRequestSchema,
  GetEventTimelineResponseSchema,
  type EventTimeline,
} from '@/gen/cortado/v1/event_timeline_pb'
import { authStore } from './auth.store'

/**
 * The event's saved run-of-show timeline. The server generates it on the
 * first read for events booked before timelines were saved. Pass an empty
 * eventId (e.g. for an undated event) to skip the request.
 */
export function useEventTimeline(eventId: string) {
  const session = useStore(authStore, (state) => state.session)
  const enabled = !!session && !!eventId

  const query = useQuery(getEventTimeline, create(GetEventTimelineRequestSchema, { eventId }), { enabled })

  return {
    timeline: query.data?.timeline,
    isLoading: query.isPending && enabled,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  }
}

/**
 * Writes a timeline returned by a mutation into the GetEventTimeline cache.
 * Staff availability takes its on-site window from the saved timeline, so the
 * Assign Staff sheet is refreshed too, and so is the job's Logistics readiness.
 */
function useSetTimelineCache() {
  const queryClient = useQueryClient()
  const transport = useTransport()
  return (timeline: EventTimeline | undefined) => {
    if (!timeline) return
    const queryKey = createConnectQueryKey({
      schema: getEventTimeline,
      transport,
      input: { eventId: timeline.eventId },
      cardinality: 'finite',
    })
    queryClient.setQueryData(queryKey, create(GetEventTimelineResponseSchema, { timeline }))
    queryClient.invalidateQueries({
      queryKey: createConnectQueryKey({ schema: listEventStaffAvailability, transport, cardinality: undefined }),
    })
    // The job's Logistics readiness reads the saved timeline.
    queryClient.invalidateQueries({
      queryKey: createConnectQueryKey({ schema: listJobEvents, transport, cardinality: undefined }),
    })
  }
}

/** Replaces the event's timeline blocks with an edited list. */
export function useUpdateEventTimelineBlocks() {
  const setCache = useSetTimelineCache()
  const mutation = useMutation(updateEventTimelineBlocks, {
    onSuccess: (response) => setCache(response.timeline),
  })
  return {
    updateBlocks: mutation.mutateAsync,
    isPending: mutation.isPending,
  }
}

/** Recalculates the event's timeline from its current details, discarding edits. */
export function useRegenerateEventTimeline() {
  const setCache = useSetTimelineCache()
  const mutation = useMutation(regenerateEventTimeline, {
    onSuccess: (response) => setCache(response.timeline),
  })
  return {
    regenerate: mutation.mutateAsync,
    isPending: mutation.isPending,
  }
}
